import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css'
import AdminLayout from '../../layouts/AdminLayout'
import ModalLayout from '../../layouts/ModalLayout'
import {
  MdArrowBack,
  MdDelete,
  MdSave,
  MdFormatListNumbered,
  MdFormatListBulleted,
  MdTitle,
  MdShortText,
  MdInfoOutline,
  MdSearch,
  MdClose,
  MdFileUpload,
  MdVisibility,
  MdEdit,
} from 'react-icons/md'
import Switch from 'react-switch'
import notify from '../../utils/IANotification'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import {
  parseRawTextToBlocks,
  extractPolicyTOC,
  detectPolicyLineKind,
  SAMPLE_POLICY_DATA
} from '../../utils/policyParser'

export default function WebsitePolicyFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  const policy_id = new URLSearchParams(window.location.search).get("policy_id")
  const isNew = !policy_id || policy_id === "new"

  const [policyData, setPolicyData] = useState({
    name: "",
    content: [], // [{ type: "header" | "subheader" | "point" | "paragraph", text: "" }]
    is_published: "1",
  })
  const [rawText, setRawText] = useState("")
  const [showImportModal, setShowImportModal] = useState(false)
  const [allPolicies, setAllPolicies] = useState([])
  const [saving, setSaving] = useState(false)
  const [contentMode, setContentMode] = useState("setting") // "setting" | "preview"
  const [tocSearch, setTocSearch] = useState("")
  const [highlightedBlockIndex, setHighlightedBlockIndex] = useState(null)

  const blockRefs = useRef({})
  const previewRefs = useRef({})
  const tocContainerRef = useRef(null)
  const tocItemRefs = useRef({})

  // Automatically adjust scroll position of the TOC container based on active/highlighted header
  useEffect(() => {
    if (highlightedBlockIndex === null || !tocContainerRef.current) return
    const activeItem = tocItemRefs.current[highlightedBlockIndex]
    const container = tocContainerRef.current

    if (activeItem && container) {
      const itemTop = activeItem.offsetTop
      const itemBottom = itemTop + activeItem.offsetHeight
      const containerTop = container.scrollTop
      const containerBottom = containerTop + container.clientHeight

      if (itemTop < containerTop + 20 || itemBottom > containerBottom - 20) {
        const targetScrollTop = itemTop - (container.clientHeight / 2) + (activeItem.offsetHeight / 2)
        container.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        })
      }
    }
  }, [highlightedBlockIndex])

  useEffect(() => {
    fetchAllPolicies()
  }, [])

  const fetchAllPolicies = async () => {
    try {
      const res = await fetch(config.endpoint + "/config", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      const data = await res.json()
      if (data.status === 200) {
        const fetchedPolicies = data?.data?.policy || []
        setAllPolicies(fetchedPolicies)

        if (!isNew && policy_id) {
          const found = fetchedPolicies.find(
            p => String(p.id) === String(policy_id) || p.identifier === policy_id
          )
          if (found) {
            let content = found.content || []
            // If content is stored as a plain string (legacy), convert
            if (typeof content === "string") {
              content = parseRawTextToBlocks(content)
            }
            setPolicyData({
              ...found,
              content: Array.isArray(content) ? content : [],
            })
          }
        }
      }
    } catch (err) {
      notify({ message: "Failed to load policy", type: "error" })
    }
  }

  // Parse raw pasted text into blocks with numbering, alphabets, and bullet detection
  const handleParseRawText = () => {
    if (!rawText.trim()) {
      notify({ message: "Please paste or type policy text first", type: "warning" })
      return
    }
    const blocks = parseRawTextToBlocks(rawText)
    setPolicyData(prev => ({ ...prev, content: blocks }))
    setShowImportModal(false)
    notify({
      message: `Successfully parsed ${blocks.length} blocks! Point form, numbering, and alphabets detected.`,
      type: "success"
    })
  }

  // Load sample church policy data
  const handleLoadSample = () => {
    if (policyData.content?.length > 0) {
      if (!window.confirm("Replace current content with the sample policy template?")) return
    }
    setPolicyData({
      name: SAMPLE_POLICY_DATA.name,
      content: SAMPLE_POLICY_DATA.content,
      is_published: "1",
    })
    setShowImportModal(false)
    notify({ message: "Sample church policy loaded successfully!", type: "success" })
  }

  const addBlock = (type = "paragraph") => {
    setPolicyData(prev => ({
      ...prev,
      content: [...prev.content, { type, text: "" }]
    }))
  }

  const updateBlock = (index, field, value) => {
    const updated = [...policyData.content]
    updated[index] = { ...updated[index], [field]: value }
    setPolicyData(prev => ({ ...prev, content: updated }))
  }

  const removeBlock = (index) => {
    setPolicyData(prev => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index)
    }))
  }

  const moveBlock = (index, direction) => {
    const arr = [...policyData.content]
    const target = index + direction
    if (target < 0 || target >= arr.length) return
    ;[arr[index], arr[target]] = [arr[target], arr[index]]
    setPolicyData(prev => ({ ...prev, content: arr }))
  }

  // Jump to block from TOC preview or search result
  const jumpToBlock = (index) => {
    setHighlightedBlockIndex(index)
    const targetElement = contentMode === "preview"
      ? previewRefs.current[index]
      : blockRefs.current[index]

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setTimeout(() => setHighlightedBlockIndex(null), 2000)
  }

  // Live Table of Contents extraction
  const liveTOC = useMemo(() => {
    return extractPolicyTOC(policyData.content || [])
  }, [policyData.content])

  // Filtered TOC items by user search query
  const filteredTOC = useMemo(() => {
    if (!tocSearch.trim()) return liveTOC
    const q = tocSearch.toLowerCase().trim()
    return liveTOC.filter(item => {
      const titleMatch = item.title?.toLowerCase().includes(q)
      const badgeMatch = item.badge?.toLowerCase().includes(q)
      const prefixMatch = item.prefix?.toLowerCase().includes(q)
      const blockText = policyData.content?.[item.index]?.text?.toLowerCase() || ""
      const contentMatch = blockText.includes(q)
      return titleMatch || badgeMatch || prefixMatch || contentMatch
    })
  }, [liveTOC, tocSearch, policyData.content])

  // Breakdown statistics for TOC
  const tocStats = useMemo(() => {
    let numbers = 0
    let alphabets = 0
    let points = 0
    let headers = 0

    liveTOC.forEach(item => {
      if (item.kind === 'number') numbers++
      else if (item.kind === 'alphabet') alphabets++
      else if (item.kind === 'point') points++
      else headers++
    })

    return { total: liveTOC.length, numbers, alphabets, points, headers }
  }, [liveTOC])

  const savePolicy = async () => {
    if (!policyData.name?.trim()) {
      notify({ message: "Policy title is required", type: "error" })
      return
    }
    setSaving(true)

    // Build full policies array for save
    let updatedPolicies
    if (isNew) {
      updatedPolicies = [
        ...allPolicies,
        {
          name: policyData.name,
          content: policyData.content,
          is_published: policyData.is_published,
        }
      ]
    } else {
      updatedPolicies = allPolicies.map(p => {
        const isMatch = String(p.id) === String(policy_id) || p.identifier === policy_id
        return isMatch
          ? { ...p, name: policyData.name, content: policyData.content, is_published: policyData.is_published }
          : p
      })
    }

    try {
      const res = await fetch(config.endpoint + "/config/policy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ policy: updatedPolicies }),
      })
      const data = await res.json()
      if (data.status === 200) {
        notify({ message: "Policy saved successfully", type: "success" })
        navigate("/admin/website/policies")
      } else {
        notify({ message: "Failed to save policy", type: "error" })
      }
    } catch (err) {
      notify({ message: "Failed to save policy", type: "error" })
    }
    setSaving(false)
  }

  const deletePolicy = async () => {
    if (!window.confirm("Delete this policy? This action cannot be undone.")) return
    setSaving(true)

    const updatedPolicies = allPolicies.map(p => {
      const isMatch = String(p.id) === String(policy_id) || p.identifier === policy_id
      return isMatch ? { ...p, is_deleted: "1" } : p
    }).filter(p => p.is_deleted !== "1")

    try {
      const res = await fetch(config.endpoint + "/config/policy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ policy: updatedPolicies }),
      })
      const data = await res.json()
      if (data.status === 200) {
        notify({ message: "Policy deleted", type: "success" })
        navigate("/admin/website/policies")
      } else {
        notify({ message: "Failed to delete policy", type: "error" })
      }
    } catch (err) {
      notify({ message: "Failed to delete policy", type: "error" })
    }
    setSaving(false)
  }

  return (
    <AdminLayout
      navSelected={"Policies"}
      toolbar={
        <>
          <button
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            onClick={() => navigate("/admin/website/policies")}
          >
            <MdArrowBack size={16} />
            Back to Policies
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              icon={MdFileUpload}
              onClick={() => setShowImportModal(true)}
            >
              Import / Parse Text
            </Button>
            {!isNew && (
              <Button variant="danger" icon={MdDelete} onClick={deletePolicy} disabled={saving}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={savePolicy} disabled={saving}>
              {saving ? "Saving..." : "Save Policy"}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-6">

        {/* Header Card */}
        <Card title={isNew ? "New Church Policy" : "Edit Policy"} subtitle="Manage policy content, structure, and visibility on the public website.">
          <div className="space-y-5">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="font-semibold text-sm text-gray-900">Published on Website</label>
                <p className="text-xs text-gray-500">When enabled, this policy is visible on the public website footer and policies directory.</p>
              </div>
              <Switch
                checked={policyData.is_published !== "0" && policyData.is_published !== false}
                onChange={(checked) => setPolicyData(prev => ({ ...prev, is_published: checked ? "1" : "0" }))}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Policy Title</label>
              <input
                type="text"
                className="text-input"
                value={policyData.name || ""}
                placeholder="e.g. Personal Data Protection Policy"
                onChange={e => setPolicyData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
          </div>
        </Card>

        {/* Side-by-Side Area: Table of Contents on Left, Content/Preview on Right */}
        <div className="grid lg:grid-cols-[340px_1fr] xl:grid-cols-[380px_1fr] gap-6 items-start">

          {/* ========================================================================= */}
          {/* LEFT: TABLE OF CONTENTS (Side-by-side with content) */}
          {/* ========================================================================= */}
          <div className="lg:sticky lg:top-20 space-y-4">
            <Card
              title="Table of Contents"
              subtitle={`${liveTOC.length} section${liveTOC.length === 1 ? '' : 's'} detected`}
              headerRight={
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setContentMode("setting")}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                      contentMode === "setting"
                        ? "bg-white text-blue-600 shadow-xs"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    title="Setting Content (Edit Blocks)"
                  >
                    <MdEdit size={13} />
                    Setting
                  </button>
                  <button
                    type="button"
                    onClick={() => setContentMode("preview")}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                      contentMode === "preview"
                        ? "bg-white text-blue-600 shadow-xs"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    title="Preview Rendered Policy"
                  >
                    <MdVisibility size={13} />
                    Preview
                  </button>
                </div>
              }
            >
              <div className="space-y-3">
                {/* Search Field */}
                <div className="relative">
                  <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none" />
                  <input
                    type="text"
                    value={tocSearch}
                    onChange={(e) => setTocSearch(e.target.value)}
                    placeholder="Search sections or clauses..."
                    className="w-full pl-9 pr-8 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                  />
                  {tocSearch && (
                    <button
                      type="button"
                      onClick={() => setTocSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear search"
                    >
                      <MdClose size={14} />
                    </button>
                  )}
                </div>

                {/* Structure stats mini pills */}
                <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                    {tocStats.numbers} Num
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100 font-medium">
                    {tocStats.alphabets} Alpha
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 font-medium">
                    {tocStats.points} Points
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-medium">
                    {tocStats.headers} Head
                  </span>
                </div>

                {/* Scrollable TOC Items */}
                {filteredTOC.length > 0 ? (
                  <div
                    ref={tocContainerRef}
                    className="max-h-[calc(100vh-22rem)] overflow-y-auto space-y-1 pr-1 divide-y divide-gray-100"
                  >
                    {filteredTOC.map((item, idx) => {
                      const indentClass =
                        item.level === 3 ? "pl-5" : item.level === 2 ? "pl-3" : "pl-1"

                      const badgeColors = {
                        number: "bg-blue-100 text-blue-700 border-blue-200",
                        alphabet: "bg-purple-100 text-purple-700 border-purple-200",
                        roman: "bg-emerald-100 text-emerald-700 border-emerald-200",
                        point: "bg-amber-100 text-amber-700 border-amber-200",
                        header: "bg-indigo-100 text-indigo-700 border-indigo-200",
                      }

                      const isSelected = highlightedBlockIndex === item.index

                      return (
                        <div
                          key={item.id || idx}
                          ref={el => {
                            if (el) tocItemRefs.current[item.index] = el
                          }}
                          onClick={() => jumpToBlock(item.index)}
                          className={`py-1.5 px-2 flex items-center justify-between gap-2 text-xs transition-colors rounded-lg cursor-pointer ${indentClass} ${
                            isSelected
                              ? "bg-blue-100 text-blue-900 font-semibold shadow-xs"
                              : "hover:bg-blue-50 text-gray-700"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wide shrink-0 ${
                                badgeColors[item.kind] || "bg-gray-100 text-gray-700 border-gray-200"
                              }`}
                            >
                              {item.badge}
                            </span>
                            <span
                              className={`truncate ${
                                item.level === 1
                                  ? "font-bold text-gray-900"
                                  : item.level === 2
                                  ? "font-semibold text-gray-800"
                                  : "text-gray-600"
                              }`}
                            >
                              {item.title}
                            </span>
                          </div>
                          <span className="text-[10px] text-blue-600 shrink-0 font-medium">
                            Jump →
                          </span>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="py-6 text-center text-gray-400 space-y-2">
                    <MdInfoOutline className="mx-auto text-2xl text-gray-300" />
                    <p className="text-xs">
                      {tocSearch
                        ? `No sections match "${tocSearch}"`
                        : "No TOC items detected yet."}
                    </p>
                    {tocSearch ? (
                      <button
                        type="button"
                        onClick={() => setTocSearch("")}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Clear Search
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowImportModal(true)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Import or paste text to generate TOC →
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* ========================================================================= */}
          {/* RIGHT: POLICY CONTENT (SETTING CONTENT OR PREVIEW) */}
          {/* ========================================================================= */}
          <div className="min-w-0 space-y-4">
            {contentMode === "setting" ? (
              /* Edit Blocks Mode (Setting the content itself) */
              <Card
                title="Policy Content Blocks"
                subtitle="Configure each policy block. The system detects numbering, alphabets, and point forms automatically."
                headerRight={
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" icon={MdTitle} onClick={() => addBlock("header")}>
                      + Header
                    </Button>
                    <Button variant="outline" size="sm" icon={MdFormatListNumbered} onClick={() => addBlock("subheader")}>
                      + Sub-Header
                    </Button>
                    <Button variant="outline" size="sm" icon={MdFormatListBulleted} onClick={() => addBlock("point")}>
                      + Point Form
                    </Button>
                    <Button variant="outline" size="sm" icon={MdShortText} onClick={() => addBlock("paragraph")}>
                      + Paragraph
                    </Button>
                  </div>
                }
              >
                {policyData.content?.length > 0 ? (
                  <div className="space-y-3">
                    {policyData.content.map((block, index) => {
                      const detected = detectPolicyLineKind(block.text)
                      const isHighlighted = highlightedBlockIndex === index

                      return (
                        <div
                          key={index}
                          ref={el => (blockRefs.current[index] = el)}
                          className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                            isHighlighted
                              ? "border-blue-600 bg-blue-50/80 shadow-md ring-2 ring-blue-300"
                              : block.type === "header"
                              ? "border-blue-400 bg-blue-50/40"
                              : block.type === "subheader"
                              ? "border-indigo-300 bg-indigo-50/30"
                              : block.type === "point"
                              ? "border-amber-300 bg-amber-50/30"
                              : "border-gray-200 bg-gray-50/50"
                          } space-y-3`}
                        >
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Block Type Buttons */}
                              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm text-xs">
                                {["header", "subheader", "point", "paragraph"].map(t => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => updateBlock(index, "type", t)}
                                    className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                                      block.type === t
                                        ? t === "header"
                                          ? "bg-blue-600 text-white shadow-xs"
                                          : t === "subheader"
                                          ? "bg-indigo-600 text-white shadow-xs"
                                          : t === "point"
                                          ? "bg-amber-600 text-white shadow-xs"
                                          : "bg-gray-700 text-white shadow-xs"
                                        : "text-gray-500 hover:text-gray-800"
                                    }`}
                                  >
                                    {t === "header"
                                      ? "HEADER"
                                      : t === "subheader"
                                      ? "SUB-HEADER"
                                      : t === "point"
                                      ? "POINT FORM"
                                      : "PARAGRAPH"}
                                  </button>
                                ))}
                              </div>

                              <span className="text-xs text-gray-400">#{index + 1}</span>

                              {/* Detected Indicator Badge */}
                              {detected && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 shadow-xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  Detected: <strong>{detected.badge}</strong>
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-700 text-sm px-1.5 py-1 rounded hover:bg-gray-200"
                                onClick={() => moveBlock(index, -1)}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-700 text-sm px-1.5 py-1 rounded hover:bg-gray-200"
                                onClick={() => moveBlock(index, 1)}
                                title="Move down"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                className="text-red-400 hover:text-red-700 text-sm px-1.5 py-1 rounded hover:bg-red-50"
                                onClick={() => removeBlock(index)}
                                title="Delete block"
                              >
                                <MdDelete />
                              </button>
                            </div>
                          </div>

                          {/* Block Text Input */}
                          {block.type === "header" ? (
                            <input
                              type="text"
                              className="text-input font-bold text-base"
                              value={block.text}
                              placeholder="Main Section Header (e.g., 1.0 INTRODUCTION & PURPOSE)..."
                              onChange={e => updateBlock(index, "text", e.target.value)}
                            />
                          ) : block.type === "subheader" ? (
                            <input
                              type="text"
                              className="text-input font-semibold text-sm"
                              value={block.text}
                              placeholder="Sub-Header (e.g., 1.1 Scope, A. Types of Personal Data, (a) Details)..."
                              onChange={e => updateBlock(index, "text", e.target.value)}
                            />
                          ) : block.type === "point" ? (
                            <div className="flex items-start gap-2">
                              <span className="text-amber-600 font-bold text-lg leading-tight pt-1.5">•</span>
                              <input
                                type="text"
                                className="text-input text-sm flex-1"
                                value={block.text.replace(/^[•*▪▫–—-]\s*/, '')}
                                placeholder="Point form item description..."
                                onChange={e => updateBlock(index, "text", `• ${e.target.value}`)}
                              />
                            </div>
                          ) : (
                            <textarea
                              className="textarea-input h-24 text-sm leading-relaxed"
                              value={block.text}
                              placeholder="Paragraph description or policy clause details..."
                              onChange={e => updateBlock(index, "text", e.target.value)}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-10 text-gray-400 space-y-3">
                    <MdInfoOutline className="mx-auto text-3xl text-gray-300" />
                    <p className="text-sm">No content blocks yet. Click "Import / Parse Text" above or add blocks manually.</p>
                    <Button variant="outline" size="sm" onClick={handleLoadSample}>
                      Load Sample Policy Template
                    </Button>
                  </div>
                )}
              </Card>
            ) : (
              /* Preview Mode (Rendered policy view) */
              <Card
                title="Policy Live Preview"
                subtitle="Formatted preview of how this policy will display to congregation and website visitors."
                headerRight={
                  <Button
                    variant="outline"
                    size="sm"
                    icon={MdEdit}
                    onClick={() => setContentMode("setting")}
                  >
                    Edit Content Blocks
                  </Button>
                }
              >
                <div className="space-y-6">
                  {/* Public Header banner simulation */}
                  <div className="p-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-300">
                        Official Church Policy
                      </span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        policyData.is_published !== "0" && policyData.is_published !== false
                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                          : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                      }`}>
                        {policyData.is_published !== "0" && policyData.is_published !== false ? "Published" : "Draft"}
                      </span>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold font-serif">
                      {policyData.name || "Untitled Policy"}
                    </h1>
                    <p className="text-xs text-gray-300">
                      Bedok Methodist Church &bull; Live Preview
                    </p>
                  </div>

                  {/* Rendered Policy Content Blocks */}
                  {policyData.content?.length > 0 ? (
                    <div className="p-6 sm:p-8 bg-white rounded-2xl border border-gray-200 space-y-5">
                      {policyData.content.map((block, idx) => {
                        const detected = detectPolicyLineKind(block.text)
                        const isHighlighted = highlightedBlockIndex === idx
                        const text = block.text || ""

                        if (block.type === "header" || detected?.kind === "header") {
                          return (
                            <div
                              key={idx}
                              ref={el => (previewRefs.current[idx] = el)}
                              className={`pt-6 pb-2 border-b border-gray-200 transition-all duration-300 ${
                                isHighlighted ? "bg-blue-50/80 p-2 rounded-lg ring-2 ring-blue-300" : ""
                              }`}
                            >
                              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-baseline gap-2">
                                {detected?.prefix && (
                                  <span className="text-blue-600 font-mono text-base font-bold">
                                    {detected.prefix}
                                  </span>
                                )}
                                <span>{detected?.title || text}</span>
                              </h2>
                            </div>
                          )
                        }

                        if (block.type === "subheader" || (detected && (detected.kind === "number" || detected.kind === "alphabet" || detected.kind === "roman"))) {
                          return (
                            <div
                              key={idx}
                              ref={el => (previewRefs.current[idx] = el)}
                              className={`pt-4 pb-1 transition-all duration-300 ${
                                isHighlighted ? "bg-blue-50/80 p-2 rounded-lg ring-2 ring-blue-300" : ""
                              }`}
                            >
                              <h3 className="text-base font-semibold text-gray-800 flex items-baseline gap-2">
                                {detected?.prefix && (
                                  <span className="text-indigo-600 font-mono text-sm font-semibold">
                                    {detected.prefix}
                                  </span>
                                )}
                                <span>{detected?.title || text}</span>
                              </h3>
                            </div>
                          )
                        }

                        if (block.type === "point" || detected?.kind === "point") {
                          const cleanPointText = text.replace(/^[•*▪▫–—-]\s*/, '')
                          return (
                            <div
                              key={idx}
                              ref={el => (previewRefs.current[idx] = el)}
                              className={`flex items-start gap-3 pl-3 py-1 transition-all duration-300 ${
                                isHighlighted ? "bg-blue-50/80 p-2 rounded-lg ring-2 ring-blue-300" : ""
                              }`}
                            >
                              <span className="text-amber-600 text-lg leading-snug shrink-0 font-bold">•</span>
                              <p className="text-sm text-gray-700 leading-relaxed">
                                {cleanPointText}
                              </p>
                            </div>
                          )
                        }

                        // Paragraph
                        return (
                          <div
                            key={idx}
                            ref={el => (previewRefs.current[idx] = el)}
                            className={`py-1 transition-all duration-300 ${
                              isHighlighted ? "bg-blue-50/80 p-2 rounded-lg ring-2 ring-blue-300" : ""
                            }`}
                          >
                            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                              {text}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center bg-gray-50 rounded-2xl border border-gray-200 text-gray-400 space-y-3">
                      <MdInfoOutline className="mx-auto text-3xl text-gray-300" />
                      <p className="text-sm">No content to preview yet.</p>
                      <Button variant="outline" size="sm" onClick={() => setContentMode("setting")}>
                        Add Content Blocks
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Import / Parse Raw Text Modal */}
      <ModalLayout
        isVisible={showImportModal}
        setIsVisible={setShowImportModal}
        modalTitle="Import Policy from Raw Text"
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-600 leading-relaxed">
            Paste raw policy document text below. The intelligent parser will automatically detect
            section headers, numbering (1.0, 1.1), alphabets (A., (a)), Roman numerals (I.),
            and bullet point forms (•, -).
          </p>

          <textarea
            className="textarea-input h-64 text-sm leading-relaxed font-mono"
            value={rawText}
            placeholder="Paste your raw policy text here...&#10;&#10;Examples detected:&#10;1.0 PURPOSE&#10;1.1 Scope of Data Collection&#10;A. Identification Data&#10;(a) NRIC and Full Name&#10;• Contact telephone numbers&#10;• Residential email"
            onChange={e => setRawText(e.target.value)}
          />

          <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-xs text-blue-800 space-y-1">
            <p className="font-semibold">Supported formats & tips:</p>
            <ul className="list-disc list-inside space-y-0.5 text-blue-700">
              <li>Numbered clauses like "1.0", "1.1", "2.3.1"</li>
              <li>Alphabetic points like "A.", "B.", "(a)", "(b)"</li>
              <li>Bullet items starting with "•", "-", "*"</li>
              <li>Capitalized title lines are parsed into main section headers</li>
            </ul>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-200">
            <Button variant="outline" size="sm" onClick={handleLoadSample}>
              Load Sample Template
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowImportModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleParseRawText}>
                Parse & Import Text
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </AdminLayout>
  )
}
